import { Stack, Typography } from '@mui/material';
import { sudoLogin } from 'API/account';
import { getAvailableProducts, getFullLicense } from 'API/license';
import { getUserInfo } from 'API/user';
import { ErrorMessages, Products, Routes } from 'enums';
import { useEmailForm } from 'hooks/use-email-form';
import { useExtensionAuth } from 'hooks/use-extension-auth';
import { usePasswordFrom } from 'hooks/use-password-form';
import i18n from 'i18n';
import jwt from 'jsonwebtoken';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useErrorStore from 'stores/error-store';
import useUserStore from 'stores/user-store';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';
import { UserInfo } from 'types/user.types';
import isEmail from 'validator/lib/isEmail';
import PaiButton from '../features/ui/base-components/pai-button';
import PaiTextField from '../features/ui/base-components/pai-text-field';

const SudoSignIn: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { extensionSignIn } = useExtensionAuth();
  const signIn = useUserStore((state) => state.signIn);
  const setToken = useUserStore((state) => state.setToken);
  const setLicenses = useUserStore((state) => state.setLicenses);
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const [loading, setLoading] = useState(false);
  const [userEmail, handleUserEmailChange] = useEmailForm(undefined, true);
  const [adminEmail, handleAdminEmailChange] = useEmailForm(undefined, true);
  const [password, handlePasswordChange] = usePasswordFrom();

  const [helperTextUsername, setHelperTextUsername] = useState('');
  const [helperTextPassword, setHelperTextPassword] = useState('');

  useEffect(() => setHelperTextUsername(''), [userEmail]);
  useEffect(() => setHelperTextPassword(''), [password, userEmail]);

  const isAllValid = () => password.inputValue !== '' && userEmail.inputValue !== '';

  const handleSignIn = async () => {
    if (!isEmail(userEmail.inputValue)) {
      setHelperTextUsername(userEmail.helperText);
      return;
    }

    setLoading(true);

    const loginRes = await sudoLogin(userEmail.inputValue, adminEmail.inputValue, password.inputValue);

    if (loginRes.error) {
      if (loginRes.error.code === 400 && loginRes.error.message === ErrorMessages.USER_NOT_EXISTS) {
        setHelperTextUsername('* ' + t('userNotFound'));
      } else if (loginRes.error.code === 401 && loginRes.error.message === ErrorMessages.WRONG_PASSWORD) {
        setHelperTextPassword('* ' + t('makeSurePasswordCorrect'));
      } else {
        handleOpenErrorDialog(loginRes?.error, '/auth/sudo-login', 'post');
      }
    }

    if (loginRes.data) {
      // Apply sudo token immediately so subsequent requests run in impersonated context.
      setToken(loginRes.data.token);
      // 1) Get user information first to retrieve the user id
      const userInfoRes = await getUserInfo();

      if (!userInfoRes.error) {
        // 2) Fetch products and licenses in parallel
        const [productsRes, licensesRes] = await Promise.all([
          getAvailableProducts(),
          getFullLicense(),
        ]);

        if (!productsRes.error) {
          const userInfo: UserInfo = new UserInfo(userInfoRes);
          const products = productsRes.products;

          const hasLicenseError = !Array.isArray(licensesRes) && 'error' in licensesRes;
          const licArr = Array.isArray(licensesRes) ? licensesRes : [];
          if (!hasLicenseError) setLicenses(licArr);

          signIn(loginRes.data.user, userInfo, products, loginRes.data.token);
        } else {
          // Fallback: if product endpoint failed but we have licenses, still sign in using licenses in token
          const userInfo: UserInfo = new UserInfo(userInfoRes);
          const decoded: any = jwt.decode(loginRes.data.token.accessToken, { complete: true });
          if (decoded && decoded.payload && decoded.payload.possessedLicenses) {
            const products: Products[] = decoded.payload.possessedLicenses;
            signIn(loginRes.data.user, userInfo, products, loginRes.data.token);
          }
        }
      } else {
        // If we cannot get user info, show error dialog
        handleOpenErrorDialog(userInfoRes?.error || { message: 'User ID missing from /me response.' }, '/users/me', 'get');
      }

      i18n.changeLanguage(loginRes.data.user.language);
      extensionSignIn(loginRes.data.token, loginRes.data.user);
      // Full page load into the customer's workspace — nothing from the previous session stays on screen.
      if (useUserStore.getState().token) {
        window.location.assign(`/dashboard${Routes.AI_CHAT}`);
        return;
      }
    }
    setLoading(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && isAllValid()) {
      handleSignIn();
    }
  };

  return (
    <div className={classes.login}>
      <div className={classes.paper}>
        <div className={classes.titleContainer}>
          <Typography className={classes.title}>SAIA</Typography>
          <Typography className={classes.title}>Admin Login</Typography>
        </div>
        <div className={classes.content}>
          <Stack gap={2}>
            <PaiTextField
              placeholder={t('userEmail')}
              value={userEmail.inputValue}
              onChange={handleUserEmailChange}
              autocomplete={'email'}
              error={helperTextUsername !== ''}
              errorText={helperTextUsername}
            />
            <PaiTextField
              placeholder={t('adminEmail')}
              value={adminEmail.inputValue}
              onChange={handleAdminEmailChange}
              autocomplete={'email'}
              error={helperTextUsername !== ''}
              errorText={helperTextUsername}
            />
            <PaiTextField
              placeholder={t('password')}
              value={password.inputValue}
              onChange={handlePasswordChange}
              type={'password'}
              autocomplete={'current-password'}
              error={helperTextPassword !== ''}
              errorText={helperTextPassword}
              onKeyDown={handleKeyDown}
            />
            <PaiButton text={t('signin')} action={handleSignIn} size={'large'} loading={loading} disabled={!isAllValid()} />
          </Stack>
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  login: {
    display: 'flex',
    justifyContent: 'center',
  },
  paper: {
    boxShadow: Shadows.GREY_SHADOW_LIGHT,
    width: '100%',
    maxWidth: '400px',
    [theme.breakpoints.down('sm')]: {
      maxWidth: 'unset',
      boxShadow: 'unset',
    },
  },
  titleContainer: {
    width: '100%',
    padding: '24px',
    backgroundColor: theme.palette.primary.main,
    backgroundImage: 'url(/dashboard/images/logos/product_logo_white.svg)',
    backgroundSize: 'auto 80%',
    backgroundRepeat: 'no-repeat',
    backgroundPositionX: 'calc(100% - 12px)',
    backgroundPositionY: 'center',
  },
  title: {
    color: '#FFF',
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 'bold',
    whiteSpace: 'pre-wrap',
  },
  content: {
    padding: '16px',
  },
  text: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.greys.medium,
  },
}));

export default SudoSignIn;
